import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {FileUtils} from "../../utils/file-utils";
import {UploadFile} from '../../documents/upload-file';
import {EntityKey} from '../../entity/entity';
import {MessageLevel, RiserUploadResponse} from '../../riser/riser-upload-response';
import {DocumentService} from '../../services/document-service';
import {RiserUploadService} from '../../services/riser-upload-service';
import {StringUtils} from '../../utils/string-utils';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogRef} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import { NgClass } from '@angular/common';

@Component({
    selector: 'riser-chart-dialog',
    templateUrl: './riser-chart-dialog.component.html',
    styleUrls: ['./riser-chart-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, NgClass, MatDialogActions, MatButton]
})
export class RiserChartDialogComponent {
  private readonly riserUploadService = inject(RiserUploadService);
  private readonly documentService = inject(DocumentService);
  readonly dialogRef = inject<MatDialogRef<RiserChartDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);


  entityKey: EntityKey;
  readonly isBusy = signal<boolean>(undefined!);
  uploadFile!: UploadFile;
  readonly uploadResponse = signal<RiserUploadResponse>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly messages = signal<string[]>(undefined!);

  constructor
  () {
    this.messages.set([]);
    this.entityKey = this.data.entityKey as EntityKey;
    this.messages.update(current => [...current, "Please select a file to upload"]);
    this.messages.update(current => [...current, "Please download a template to get started"]);
  }

  getTemplate(): void {
    this.isBusy.set(true);
    this.riserUploadService.getTemplate(this.entityKey).subscribe({
      next: (template: Blob) => {
        FileUtils.downloadFile(template, this.entityKey.entityId + "-Riser_Chart.xlsx");
        this.isBusy.set(false);
        this.messages.update(current => [...current, "Please add riser information to the downloaded file and click the 'Select File' button to upload"]);
      }, error: (error: any) => this.handleError(error)
    });
  }

  fileChange(upload: any) {
    this.uploadResponse.set(null!);
    this.isBusy.set(true);
    let reader = new FileReader();
    this.messages.set([]);
    if (upload.target.files && upload.target.files.length > 0) {
      let file = upload.target.files[0];
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        let mimeType = file.type || this.getMimeType(file.name);
        this.uploadFile = new UploadFile();
        this.uploadFile.documentName = file.name;
        this.uploadFile.entityKey = this.entityKey;
        this.uploadFile.fileBytes = reader.result as unknown as Uint8Array;
        this.uploadFile.fileName = file.name;
        this.uploadFile.mimeType = mimeType;
        this.uploadFile.documentType = "Unit Information";
        this.isBusy.set(false);
        this.messages.update(current => [...current, "Please click the 'Upload Riser Chart' button to upload the riser values"]);
      };
    }
  }

  private getMimeType(fileName: string): string {
    let mimeType: string = "text/plain";
    if (fileName && fileName.length > 2) {
      switch (fileName.slice(-3).toLowerCase()) {
        case "eml":
          mimeType = "message/rfc822";
          break;
      }
    }
    return mimeType;
  }

  uploadRisers(): void {
    this.messages.set([]);
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.riserUploadService.uploadRisers(this.entityKey, this.uploadFile.fileBytes).subscribe({
      next: (result: RiserUploadResponse) => {
        this.messages.set(result.messages);
        this.uploadResponse.set(result);
        if (this.uploadResponse().messageLevel == MessageLevel.INFO) {
          let uploadFiles: UploadFile[] = [];
          uploadFiles.push(this.uploadFile);
          this.documentService.uploadFiles(uploadFiles).subscribe({
            next: () => {
              this.messages.update(current => [...current, "Success!"]);
              this.dialogRef.close(this.entityKey);
            }, error: (error: any) => this.handleError(error)
          });
        } else {
          this.isBusy.set(false);
        }
      }, error: (error: any) => this.handleError(error)
    });
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    if (!this.uploadResponse()) {
      this.uploadResponse.set(new RiserUploadResponse());
    }
    this.uploadResponse().messageLevel = MessageLevel.ERROR;
    this.messages.set([]);
    this.messages.update(current => [...current, this.errorMessage()]);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  isReadyToUpload(): boolean {
    return this.uploadFile != null;
  }

  getMessageClass(): string {
    if (this.uploadResponse()) {
      switch (this.uploadResponse().messageLevel) {
        case MessageLevel.ERROR:
          return "error";
        case MessageLevel.INFO:
          return "info";
        case MessageLevel.WARNING:
          return "warn";
      }
    } else {
      return "info";
    }
  }

}
